import { useLayoutEffect, useRef, useState } from "react";
import { PageHeader } from "../components/ui.tsx";
import { useMe, useSession } from "../lib/session.tsx";
import type { Permission } from "../api/types.ts";
import { fmt, useLang, useT, type Dict } from "../i18n/index.tsx";
import markers from "./help-markers.json";
import "./help.css";

type SectionKey = keyof Dict["help"]["sections"];
type Box = { x: number; y: number; w: number; h: number; at?: Spot };
/** Screenshot size (CSS px) and the numbered boxes on it, in % of the picture; made by the capture script per language. */
type Shot = { w: number; h: number; boxes: (Box | null)[] };

/** Shown when the user can do that task; Telegram approvals are for owners only. */
const SECTIONS: { key: SectionKey; perms: Permission[]; owner?: boolean }[] = [
  { key: "deposits", perms: ["deposits:view"] },
  { key: "payout", perms: ["payouts:create"] },
  { key: "approve", perms: ["payouts:approve"] },
  { key: "topup", perms: ["vault:view"] },
  { key: "statement", perms: ["statement:view"] },
  { key: "invite", perms: ["team:invite"] },
  { key: "telegram", perms: [], owner: true },
];

/** Ask the app shell to open the getting-started guide again (it owns the guide's state). */
export const restartTour = () => window.dispatchEvent(new Event("icn:tour"));

export function Help() {
  const t = useT();
  const { lang } = useLang();
  const me = useMe();
  const { canAny } = useSession();
  const all = markers as Record<string, Record<string, Shot>>;
  const shotLang = all[lang] ? lang : "en";
  const shots = all[shotLang] ?? {};
  const shown = SECTIONS.filter((s) => (s.owner ? me.role.key === "owner" : canAny(...s.perms)) && shots[s.key]);

  return (
    <div className="page">
      <PageHeader title={t.help.title}>{t.help.intro}</PageHeader>

      <section className="card help-tour">
        <div>
          <h2 className="card-title">{t.help.tourTitle}</h2>
          <p className="card-sub">{t.help.tourBody}</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={restartTour}>{t.help.tourStart}</button>
      </section>

      <nav className="help-toc" aria-label={t.help.title}>
        {shown.map((s) => (
          <a key={s.key} href={`#help-${s.key}`} className="btn btn-ghost btn-sm">{t.help.sections[s.key].title}</a>
        ))}
      </nav>

      {shown.map((s) => {
        const c = t.help.sections[s.key];
        const shot = shots[s.key]!;
        return (
          <section key={s.key} id={`help-${s.key}`} className="card section-block help-sec" aria-labelledby={`help-${s.key}-h`}>
            <h2 id={`help-${s.key}-h`} className="card-title">{c.title}</h2>
            <p className="card-sub">{c.lead}</p>
            {/* Whole-page pictures get the full width, with the steps underneath. */}
            <div className={`help-body ${shot.w > 800 ? "wide" : ""}`}>
              <Figure shot={shot} src={`/help/${shotLang}/${s.key}.webp`} alt={fmt(t.help.figAlt, { title: c.title })} />
              <ol className="help-steps">
                {c.steps.map((step, i) => (
                  <li key={i}>
                    <span className="help-num" aria-hidden="true">{i + 1}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        );
      })}
    </div>
  );
}

/** Number badge size (px, matches help.css) and its gap from the box. */
const BADGE = { lg: 24, sm: 18, gap: 4 };
type Spot = "r" | "re" | "tr" | "te" | "br" | "be" | "l" | "le" | "tl";

/**
 * Centre of a box's number in px of the picture as drawn. The capture script picks the spot per box (`at`) where the
 * number covers no text, field, icon or button at the most crowded size the picture is drawn: r = right of the box,
 * tr / br = above / below its right corner, l = left of it; an "e" suffix (re, te, be, le) sits it on that edge; tl = the corner.
 */
function badgeAt(b: Box, W: number, H: number, size: number) {
  const g = BADGE.gap, h = size / 2;
  const l = (b.x / 100) * W, t = (b.y / 100) * H, r = ((b.x + b.w) / 100) * W, bt = ((b.y + b.h) / 100) * H, cy = (t + bt) / 2;
  const spots: Record<Spot, [number, number]> = {
    r: [r + g + h, cy], re: [r, cy], tr: [r - h, t - g - h], te: [r - h - g, t], br: [r - h, bt + g + h], be: [r - h - g, bt],
    l: [l - g - h, cy], le: [l, cy], tl: [l, t],
  };
  const [x, y] = spots[b.at ?? "re"];
  return { x, y };
}

function Figure({ shot, src, alt }: { shot: Shot; src: string; alt: string }) {
  const ref = useRef<HTMLElement>(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const size = matchMedia("(max-width: 760px)").matches ? BADGE.sm : BADGE.lg;
  const H = (w * shot.h) / shot.w;
  return (
    /* Pictures are not mirrored for right-to-left languages, so the boxes stay left-to-right too. */
    <figure ref={ref} className="help-fig" dir="ltr" style={{ maxWidth: shot.w }}>
      <img src={src} alt={alt} width={shot.w} height={shot.h} loading="lazy" />
      {shot.boxes.map((b, i) =>
        b ? <span key={i} className="help-mark" style={{ left: `${b.x}%`, top: `${b.y}%`, width: `${b.w}%`, height: `${b.h}%` }} aria-hidden="true" /> : null,
      )}
      {w > 0 && shot.boxes.map((b, i) => {
        if (!b) return null;
        const p = badgeAt(b, w, H, size);
        return <b key={i} className="help-badge" style={{ left: p.x, top: p.y }} aria-hidden="true">{i + 1}</b>;
      })}
    </figure>
  );
}
